import { initialState, persistedRecordingAdapter } from "./state";
import { caseViewerActions } from "src/features/cases/caseViewer/store/slice";
import {
    createSlice,
    createAsyncThunk,
    EntityId,
    PayloadAction,
} from "@reduxjs/toolkit";

import { reIdSelectors } from "./selectors";
import { RootState } from "app/store";
import { caseManagerApi } from "services/streamingApi/caseManager.api";
import { playbackSelectors } from "features/cases/caseViewer/store/selectors.playback";
import { DateTime } from "luxon";
import { PersistedRecording } from "types/PersistedRecording";
import { ObjectDetectionFrame } from "types/reId/ObjectDetectionFrame";
import { VectorSearchResultRecord } from "types/reId/VectorSearchResultRecord";
import { UserPlayMode } from "features/playback/types/UserPlayMode";
import { ObjectDetectionModelName } from "types/ObjectDetectionModelName";
import { ObjectDetectionModel } from "types/reId/ObjectDetectionModel";
import { ReIdentificationModelSelection } from "types/reId/ReIdentificationModelSelection";

// thunks
const pausePlaybackAndSaveUserPlaybackMode = createAsyncThunk(
    "reId/pausePlaybackAndSaveUserPlaybackMode",
    async (payload: undefined, { getState, dispatch }) => {
        const state = getState() as RootState;

        const userSelectedPlayMode =
            playbackSelectors.selectUserRequestedPlayMode(state);

        await dispatch(
            reIdActions.setSavedUserPlaybackMode(userSelectedPlayMode),
        );

        await dispatch(caseViewerActions.pausePlayback());
    },
);

const reIdViewStartedAsync = createAsyncThunk(
    "reId/reIdViewStartedAsync",
    async (
        payload: {
            activeDossierDeviceId: EntityId;
            activePersistedRecordingId: EntityId;
        },
        { dispatch },
    ) => {
        await dispatch(pausePlaybackAndSaveUserPlaybackMode());

        // set the active dossier device id in the store so it is the only one displayed.
        await dispatch(
            reIdActions.setReIdViewModeIsActive({
                viewModeActive: true,
                activeDossierDeviceId: payload.activeDossierDeviceId,
                activePersistedRecordingId: payload.activePersistedRecordingId,
            }),
        );
    },
);

const reIdViewStopAsync = createAsyncThunk(
    "reId/reIdViewStopAsync",
    async (payload, { getState, dispatch }) => {
        await dispatch(
            reIdActions.setReIdViewModeIsActive({
                viewModeActive: false,
            }),
        );

        const state = getState() as RootState;

        const savedPlaybackMode =
            reIdSelectors.selectSavedUserPlaybackMode(state);

        if (savedPlaybackMode === "play" || savedPlaybackMode === "resume") {
            await dispatch(caseViewerActions.startPlayback());
        }
    },
);

const showDetectedObjectInLiveMode = createAsyncThunk(
    "reId/showDetectedObjectInLiveMode",
    async (
        payload: { startTimeForTimeline: string },
        { getState, dispatch },
    ) => {
        const state = getState() as RootState;

        dispatch(
            caseViewerActions.setPlaybackStartDateTime({
                playbackStartDateTime: payload.startTimeForTimeline,
            }),
        );

        await dispatch(
            reIdActions.setReIdViewModeIsActive({
                viewModeActive: false,
                activeDossierDeviceId: undefined,
            }),
        );

        const vectorSearchResults =
            reIdSelectors.selectVectorSearchResults(state);

        await dispatch(
            caseViewerActions.setVisibilityDossierDevices({
                dossierDeviceIds: [
                    ...new Set(
                        vectorSearchResults.map((x) => x.dossierDeviceId),
                    ),
                ],
                visible: true,
            }),
        );
    },
);

const reIdFetchObjectDetectionsEveryRangeAsync = createAsyncThunk(
    "reId/reIdFetchObjectDetectionsEveryRangeAsync",
    async (
        payload: {
            dossierId: EntityId;
            timeRangeInSeconds: number;
            objectDetectionModelName: ObjectDetectionModelName;
        },
        { dispatch, getState, rejectWithValue },
    ) => {
        const state = getState() as RootState;

        const currentPlaybackTimestamp =
            playbackSelectors.selectCurrentPlaybackTimeStamp(state);

        const playbackDateTime = DateTime.fromMillis(currentPlaybackTimestamp);
        const stopTime = playbackDateTime.plus({
            seconds: payload.timeRangeInSeconds,
        });

        const { data, isSuccess, error, isError } = await dispatch(
            caseManagerApi.endpoints.getObjectDetectionsByStartAndStoptime.initiate(
                {
                    dossierId: payload.dossierId,
                    startTime: playbackDateTime.toISO(),
                    stopTime: stopTime.toISO(),
                    objectDetectionModelName: payload.objectDetectionModelName,
                },
            ),
        );

        if (isSuccess) {
            dispatch(reIdActions.setDetectedObjects(data));
        }

        if (isError) {
            dispatch(reIdActions.setDetectedObjects([]));

            return rejectWithValue({
                message: `Error while retrieving object detections for dossier ${payload.dossierId}: ${error}`,
            });
        }
    },
);

const reIdFetchPersistedRecordingsForActiveDetectedObjectIdAsync =
    createAsyncThunk(
        "reId/reIdFetchPersistedRecordingsForActiveDetectedObjectIdAsync",
        async (
            payload: {
                dossierId: EntityId;
                selectedDetectedObjectFrame: ObjectDetectionFrame;
                maxDistance: number;
                objectDetectionModelName: ObjectDetectionModelName;
            },
            { dispatch, rejectWithValue },
        ) => {
            dispatch(reIdActions.setLoadingVectorSearchResults(true));

            const { data, isSuccess, error, isError } = await dispatch(
                caseManagerApi.endpoints.getPersistedRecordingsByDetectedObjectIdId.initiate(
                    {
                        dossierId: payload.dossierId,
                        persistedRecordingId:
                            payload.selectedDetectedObjectFrame
                                .persistedRecordingId,
                        trackerId:
                            payload.selectedDetectedObjectFrame.trackerId,
                        classId: payload.selectedDetectedObjectFrame.classId,
                        maxDistance: payload.maxDistance,
                        objectDetectionModelName:
                            payload.objectDetectionModelName,
                    },
                ),
            );

            if (isSuccess) {
                dispatch(reIdActions.setVectorSearchResults(data));

                const referenceIdentity = data.find(
                    (record) => record.isReferenceIdentity,
                );

                if (referenceIdentity) {
                    dispatch(
                        reIdActions.setActiveVectorSearchResultRecordToPlay(
                            referenceIdentity,
                        ),
                    );
                }
            }

            if (isError) {
                return rejectWithValue({
                    message: `Error while retrieving vector search results for dossier ${payload.dossierId}: ${error}`,
                });
            }

            dispatch(reIdActions.setLoadingVectorSearchResults(false));
        },
    );

// slice
export const reIdSlice = createSlice({
    name: "reId",
    initialState,
    reducers: {
        resetState: () => {
            return { ...initialState };
        },
        setReIdViewModeIsActive: (
            state,
            action: PayloadAction<{
                viewModeActive: boolean;
                activeDossierDeviceId?: EntityId;
                activePersistedRecordingId?: EntityId;
            }>,
        ) => {
            state.reIdViewModeIsActive = action.payload.viewModeActive;
            state.activeDossierDeviceId = action.payload.viewModeActive
                ? action.payload.activeDossierDeviceId
                : undefined;
            state.activePersistedRecordingId = action.payload.viewModeActive
                ? action.payload.activePersistedRecordingId
                : undefined;
        },
        setSavedUserPlaybackMode: (
            state,
            action: PayloadAction<UserPlayMode | undefined>,
        ) => {
            state.savedUserPlaybackMode = action.payload;
        },
        setActiveDetectedObjectPersistedRecordings: (
            state,
            action: PayloadAction<PersistedRecording[]>,
        ) => {
            state.activeDetectedObjectPersistedRecordings =
                persistedRecordingAdapter.setAll(
                    state.activeDetectedObjectPersistedRecordings,
                    action.payload,
                );
        },
        setActiveVectorSearchResultRecordToPlay: (
            state,
            action: PayloadAction<VectorSearchResultRecord | undefined>,
        ) => {
            state.activeVectorSearchResultRecordToPlay = action.payload;
        },
        setSelectedDetectedObjectFrame: (
            state,
            action: PayloadAction<ObjectDetectionFrame | undefined>,
        ) => {
            state.selectedDetectedObjectFrame = action.payload;
        },
        setObjectDetectionModels: (
            state,
            action: PayloadAction<ObjectDetectionModel[]>,
        ) => {
            state.objectDetectionModels = action.payload;
        },
        setReIdentificationModelSelection: (
            state,
            action: PayloadAction<ReIdentificationModelSelection | undefined>,
        ) => {
            if (
                action.payload &&
                action.payload.selectedObjectDetectionModelNames.length > 0 &&
                !state.reIdentificationModelSelection
            ) {
                // set the default selection
                state.selectedObjectDetectionModelName =
                    action.payload.selectedObjectDetectionModelNames[0];
            }

            state.reIdentificationModelSelection = action.payload;
        },
        setSelectedObjectDetectionModelName: (
            state,
            action: PayloadAction<ObjectDetectionModelName>,
        ) => {
            state.selectedObjectDetectionModelName = action.payload;
        },
        setLoadingVectorSearchResults: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.loadingVectorSearchResults = action.payload;
        },
        setVectorSearchResults: (
            state,
            action: PayloadAction<VectorSearchResultRecord[]>,
        ) => {
            state.vectorSearchResults = action.payload;
        },
        setDetectedObjects: (
            state,
            action: PayloadAction<ObjectDetectionFrame[]>,
        ) => {
            state.detectedObjects = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addMatcher(
            caseManagerApi.endpoints.getAllObjectDetectionModels.matchFulfilled,
            (state, action) => {
                state.objectDetectionModels = action.payload;
            },
        );
    },
});

// exports
export const reIdThunks = {
    reIdViewStartedAsync,
    reIdViewStopAsync,
    reIdFetchObjectDetectionsEveryRangeAsync,
    reIdFetchPersistedRecordingsForActiveDetectedObjectIdAsync,
    showDetectedObjectInLiveMode,
    pausePlaybackAndSaveUserPlaybackMode,
};
export const reIdActions = reIdSlice.actions;

export default reIdSlice.reducer;
